import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  dismissLead,
  IntakeLeadRowDetailsDto,
  markLeadContacted,
} from '../../api/IntakeHomeApi';

/**
 * What staff can do with a lead on Intake home (4 Nov 2026): mark it contacted, or dismiss it.
 * Neither sends anything to the prospect, and neither creates a client or case.
 */
interface LeadActionsProps {
  lead: IntakeLeadRowDetailsDto;
  clientName: string;
  formatDateTime: (iso: string | null) => string;
  onChanged: () => void | Promise<void>;
}

type Mode = 'contacted' | 'dismiss' | null;

const LeadActions: React.FC<LeadActionsProps> = ({ lead, clientName, formatDateTime, onChanged }) => {
  const [mode, setMode] = useState<Mode>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (saving) return;
    setMode(null);
    setNote('');
    setError(null);
  };

  const submit = async () => {
    if (!mode) return;
    setSaving(true);
    setError(null);
    try {
      const text = note.trim() || undefined;
      if (mode === 'contacted') await markLeadContacted(lead.leadGuid, text);
      else await dismissLead(lead.leadGuid, text);
      setMode(null);
      setNote('');
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {lead.status === 'CONTACTED' && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          Contacted{lead.statusBy ? ` by ${lead.statusBy}` : ''}
          {lead.statusAt ? `, ${formatDateTime(lead.statusAt)}` : ''}
        </Typography>
      )}
      {lead.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {lead.note}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={() => setMode('contacted')}>
          {lead.status === 'CONTACTED' ? 'Contacted again' : 'Mark contacted'}
        </Button>
        <Button size="small" color="inherit" onClick={() => setMode('dismiss')}>
          Dismiss
        </Button>
      </Stack>

      <Dialog open={mode !== null} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>
          {mode === 'contacted' ? `Mark ${clientName} contacted` : `Dismiss ${clientName}?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {mode === 'contacted'
              ? 'The lead stays on the list until it is dismissed or becomes a client.'
              : 'The lead leaves the Intake home list. Nothing is sent to them.'}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label={mode === 'contacted' ? 'Note (optional)' : 'Reason (optional)'}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            multiline
            minRows={2}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={mode === 'dismiss' ? 'inherit' : 'primary'}
            onClick={() => void submit()}
            disabled={saving}
          >
            {mode === 'contacted' ? 'Save' : 'Dismiss'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadActions;
